import { Box, Divider, Typography, styled } from '@mui/material';
import React from 'react';
import { useTranslate } from 'src/locales';

interface EventDateProps {
  startDate: Date | string;
  endDate?: Date | string;
  onlyStart?: boolean;
  timezone?: string;
}

const DateBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: 15,
  paddingLeft: 15,
  backgroundColor: 'rgba(0, 0, 0,0.4)',
  border: '0.1px solid #fff',
  paddingTop: 5,
  paddingBottom: 5,
}));

const EventDate: React.FC<EventDateProps> = ({
  startDate,
  endDate,
  onlyStart,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
  const { i18n } = useTranslate();

  // Parse dates - handle both string and Date inputs
  const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
  let endDateObj = null;
  if (endDate) {
    endDateObj = endDate instanceof Date ? endDate : new Date(endDate);
  }

  // Format options with timezone
  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
  };

  // Format the month and day for the date box
  const monthStr = startDateObj
    .toLocaleString(i18n?.language ?? 'en-US', {
      ...dateFormatOptions,
      month: 'short',
    })
    .toUpperCase();

  const dayStr = startDateObj.toLocaleString(i18n?.language ?? 'en-US', {
    ...dateFormatOptions,
    day: 'numeric',
  });

  // Helper function to check if a date is today in the specified timezone
  const isDateToday = (date: Date): boolean => {
    const today = new Date();
    const dateInTz = new Date(date.toLocaleString(i18n?.language ?? 'en-US', dateFormatOptions));
    const todayInTz = new Date(today.toLocaleString(i18n?.language ?? 'en-US', dateFormatOptions));

    return (
      dateInTz.getFullYear() === todayInTz.getFullYear() &&
      dateInTz.getMonth() === todayInTz.getMonth() &&
      dateInTz.getDate() === todayInTz.getDate()
    );
  };

  // Helper function to check if a date is tomorrow in the specified timezone
  const isDateTomorrow = (date: Date): boolean => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateInTz = new Date(date.toLocaleString(i18n?.language ?? 'en-US', dateFormatOptions));
    const tomorrowInTz = new Date(
      tomorrow.toLocaleString(i18n?.language ?? 'en-US', dateFormatOptions)
    );

    return (
      dateInTz.getFullYear() === tomorrowInTz.getFullYear() &&
      dateInTz.getMonth() === tomorrowInTz.getMonth() &&
      dateInTz.getDate() === tomorrowInTz.getDate()
    );
  };

  // Determine if the event is today, tomorrow, or a specific day
  const getDateLabel = () => {
    if (isDateToday(startDateObj)) {
      return i18n?.language === 'fr' ? 'Aujourd’hui' : 'Today';
    }
    if (isDateTomorrow(startDateObj)) {
      return i18n?.language === 'fr' ? 'Demain' : 'Tomorrow';
    }

    const dayName = startDateObj.toLocaleString(i18n?.language ?? 'en-US', {
      ...dateFormatOptions,
      weekday: 'long',
    });

    const fullDate = startDateObj.toLocaleString(i18n?.language ?? 'en-US', {
      ...dateFormatOptions,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

    const now = new Date();
    const diffTime = startDateObj.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays < 7 ? dayName : fullDate;
  };
  // Format the time range with timezone if needed
  const getTimeRange = (isOnlyStart?: boolean) => {
    const startTime = startDateObj.toLocaleString(i18n?.language ?? 'en-US', {
      ...dateFormatOptions,
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

    // Get timezone abbreviation
    const timezoneName = startDateObj
      .toLocaleString(i18n?.language ?? 'en-US', {
        ...dateFormatOptions,
        timeZoneName: 'shortGeneric',
      })
      .split(' ')
      .pop();

    if (endDateObj && !isOnlyStart) {
      const endTime = endDateObj.toLocaleString(i18n?.language ?? 'en-US', {
        ...dateFormatOptions,
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });

      // Check if start and end dates are on the same day
      const startDay = startDateObj.toLocaleString(i18n?.language ?? 'en-US', {
        ...dateFormatOptions,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });

      const endDay = endDateObj.toLocaleString(i18n?.language ?? 'en-US', {
        ...dateFormatOptions,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });

      // If the end date is on a different day, include the date
      if (startDay !== endDay) {
        const endDateFormatted = endDateObj.toLocaleString(i18n?.language ?? 'en-US', {
          ...dateFormatOptions,
          month: 'short',
          day: 'numeric',
        });

        return `${startTime} - ${endDateFormatted}, ${endTime} ${timezoneName}`;
      }

      return `${startTime} - ${endTime} ${timezoneName}`;
    }

    return `${startTime} ${timezoneName}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        color: 'white',
        borderRadius: 1,
        overflow: 'hidden',
        width: 'fit-content',
      }}
    >
      <DateBox>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {monthStr}
        </Typography>
        <Divider sx={{ width: '100%', borderWidth: 1.2 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {dayStr}
        </Typography>
      </DateBox>

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {getDateLabel()}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          {getTimeRange(onlyStart)}
        </Typography>
      </Box>
    </Box>
  );
};

export default EventDate;
